// LootjesDeelnemen.jsx — /lootjes/doe-mee/:groepToken
// Deelnemer vult naam + interesses in en wordt toegevoegd aan de groep

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { INTERESTS } from "../constants";
import { Events } from "../analytics";

export default function LootjesDeelnemen() {
  const { groepToken } = useParams();
  const [groep, setGroep] = useState(null);
  const [fout, setFout] = useState(null);
  const [laden, setLaden] = useState(true);
  const [stap, setStap] = useState(1); // 1=naam+email, 2=interesses, 3=klaar
  const [naam, setNaam] = useState("");
  const [email, setEmail] = useState("");
  const [interesses, setInteresses] = useState([]);
  const [interestsExtra, setInterestsExtra] = useState("");
  const [verzenden, setVerzenden] = useState(false);
  const [lootjeUrl, setLootjeUrl] = useState(null);

  useEffect(() => {
    try {
      const data = JSON.parse(atob(groepToken.replace(/-/g, "+").replace(/_/g, "/")));
      if (data.getrokken) {
        setFout("De lootjes zijn al getrokken voor deze groep.");
      } else {
        setGroep(data);
      }
    } catch {
      setFout("Deze link is ongeldig.");
    } finally {
      setLaden(false);
    }
  }, [groepToken]);

  async function doeMee() {
    if (verzenden) return;
    setVerzenden(true);
    try {
      const res = await fetch("/.netlify/functions/lootjes?join=1", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groepToken,
          naam: naam.trim(),
          email: email || null,
          interesses,
          interestsExtra,
        }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setLootjeUrl(json.deelnemerUrl);
      setStap(3);
      Events.track("Lootjes deelnemer toegevoegd");
    } catch (e) {
      alert(e.message || "Er ging iets mis. Probeer opnieuw.");
      setVerzenden(false);
    }
  }

  if (laden) return (
    <main className="main">
      <div className="card fi" style={{ textAlign: "center", padding: 40 }}>
        <div className="ai-spinner" style={{ margin: "0 auto 16px" }} />
        <div style={{ color: "#8A7B73" }}>Laden…</div>
      </div>
    </main>
  );

  if (fout) return (
    <main className="main">
      <div className="card fi" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
        <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, marginBottom: 8 }}>Oeps</h2>
        <p style={{ color: "#8A7B73", lineHeight: 1.6 }}>{fout}</p>
      </div>
    </main>
  );

  // Stap 1: naam + email
  if (stap === 1) return (
    <main className="main">
      <div className="card fi">
        <div style={{
          background: "linear-gradient(135deg,#C9856A,#A86349)",
          borderRadius: 20, padding: 20, marginBottom: 22, textAlign: "center",
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎁</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "white", marginBottom: 4 }}>
            {groep?.naam}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>
            Budget: {groep?.budget === "100plus" ? "€100+" : groep?.budget === "15" ? "tot €15" : groep?.budget === "30" ? "€15–€30" : groep?.budget === "60" ? "€30–€60" : "€60–€100"}
            {groep?.datum ? ` · ${new Date(groep.datum).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}` : ""}
          </div>
        </div>

        <div className="tag">Stap 1 van 2</div>
        <h1 className="step">Doe jij <em>mee</em>?</h1>
        <p className="sub">Vul je naam en interesses in — dan weet de trekker precies wat jij leuk vindt.</p>

        <div className="flabel">Jouw naam</div>
        <input className="inp" placeholder="Voornaam" value={naam}
          onChange={e => setNaam(e.target.value)} autoFocus />

        <div className="flabel">
          E-mailadres <span style={{ color: "#8A7B73", fontWeight: 400 }}>(optioneel)</span>
        </div>
        <input className="inp" placeholder="jouw@email.nl" type="email"
          value={email} onChange={e => setEmail(e.target.value)} />
        <p className="sub" style={{ fontSize: 12, marginTop: -10, marginBottom: 20 }}>
          Handig — dan sturen we je lootje ook per mail.
        </p>

        <button className={"bfull bn " + (naam.trim() ? "on" : "off")}
          onClick={() => naam.trim() && setStap(2)}>
          Verder →
        </button>
      </div>
    </main>
  );

  // Stap 2: interesses
  if (stap === 2) return (
    <main className="main">
      <div className="card fi">
        <div className="tag">Stap 2 van 2</div>
        <h1 className="step">Wat vind <em>jij</em> leuk?</h1>
        <p className="sub">
          Kies 2 interesses zodat jouw trekker goede suggesties krijgt.
          {interesses.length === 2 && <span style={{ color: "#3D5A4C", fontWeight: 600 }}> ✓ Perfect!</span>}
        </p>
        <div className="chips">
          {INTERESTS.map(i => {
            const sel = interesses.includes(i.id);
            return (
              <div key={i.id} className={"chip" + (sel ? " sel" : "")}
                onClick={() => {
                  const next = sel ? interesses.filter(x => x !== i.id)
                    : interesses.length < 2 ? [...interesses, i.id] : interesses;
                  setInteresses(next);
                }}>
                <span>{i.e}</span>{i.l}
              </div>
            );
          })}
        </div>

        <div className="flabel" style={{ marginTop: 4 }}>
          Nog iets toevoegen? <span style={{ color: "#8A7B73", fontWeight: 400 }}>(optioneel)</span>
        </div>
        <input className="inp" placeholder='bijv. "houdt van Japanse whisky"'
          value={interestsExtra}
          onChange={e => setInterestsExtra(e.target.value)}
          style={{ marginBottom: 18 }} />

        <div className="brow">
          <button className="bbk" onClick={() => setStap(1)}>← Terug</button>
          <button className={"bn " + (interesses.length === 2 ? "gr" : "off")}
            onClick={doeMee} disabled={verzenden}>
            {verzenden ? "Opslaan…" : "Ik doe mee ✓"}
          </button>
        </div>
      </div>
    </main>
  );

  // Stap 3: klaar
  return (
    <main className="main">
      <div className="card fi">
        <div style={{
          background: "linear-gradient(135deg,#3D5A4C,#4a6b5a)",
          borderRadius: 20, padding: 24, marginBottom: 22, textAlign: "center",
        }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "white", marginBottom: 6 }}>
            Welkom, {naam}!
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.7 }}>
            Je bent toegevoegd aan {groep?.naam}. Zodra de organisator de lootjes trekt ontvang je een seintje.
          </div>
        </div>

        {lootjeUrl && (
          <div style={{
            background: "#FDEEE8", border: "1.5px solid #EDE3D9",
            borderRadius: 14, padding: "14px 16px", marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#2C2420", marginBottom: 4 }}>
              🔗 Bewaar jouw persoonlijke link
            </div>
            <div style={{ fontSize: 12, color: "#8A7B73", marginBottom: 10 }}>
              Via deze link zie je straks jouw lootje en de suggesties.
            </div>
            <button onClick={() => navigator.clipboard.writeText(lootjeUrl)}
              style={{
                width: "100%", padding: "10px 0", borderRadius: 50,
                border: "1.5px solid #C9856A", background: "white",
                color: "#C9856A", fontWeight: 700, fontSize: 13,
                cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
              }}>
              📋 Kopieer mijn link
            </button>
          </div>
        )}

        <div style={{
          background: "#EDF3EF", border: "1px solid rgba(61,90,76,0.15)",
          borderRadius: 14, padding: "14px 16px", textAlign: "center",
        }}>
          <div style={{ fontSize: 13, color: "#2C2420", lineHeight: 1.6 }}>
            <strong>Wil jij ook nooit een verjaardag vergeten?</strong><br/>
            Givn stuurt je tijdig een herinnering met cadeausuggesties.
          </div>
          <a href="/" style={{
            display: "inline-block", marginTop: 12, background: "#C9856A",
            color: "white", textDecoration: "none", padding: "11px 24px",
            borderRadius: 50, fontWeight: 700, fontSize: 14,
          }}>
            Probeer Givn gratis →
          </a>
        </div>
      </div>
    </main>
  );
}
